//==============================================================================
// Displays one or more substitute products if the current product is out of stock
//
// Design decisions:
//  Show if any variants are out of stock? Or just the currently selected variant?
//  Needs to work in conjunction with "inventory visibility" feature -- ensure
//    that one operates the data action level -- PROBABLY NOT POSSIBLE
//
// Start loading the products during initial client render
// Toggle display based on inventory of current product
//   Wrap the Add to Cart component's shouldShowOutOfStock function to determine visibility
//   Don't emit events related to the loading checks -- leave display as-is.
//   For all others, use results of shouldShowOutOfStock
//==============================================================================
import * as React from 'react';
import { observer } from 'mobx-react';

import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy';

import { ISubstituteProductsData } from './substitute-products.data';
import { ISubstituteProductsProps } from './substitute-products.props.autogenerated';

//==============================================================================
// INTERFACES
//==============================================================================
export interface ISubstituteProductsViewProps extends ISubstituteProductsProps<ISubstituteProductsData> {
    productList: SubstituteProduct[] | undefined;
    showSubstitute: Boolean;
}

export interface SubstituteProduct {
    name: string;
    id: number;
    price: string | undefined;
}

//==============================================================================
// CLASS DEFINITION
//==============================================================================
/**
 *
 * SubstituteProducts component
 * @extends {React.Component<ISubstituteProductsProps<ISubstituteProductsData>>}
 */
@observer
class SubstituteProducts extends React.Component<ISubstituteProductsProps<ISubstituteProductsData>> {

    //----------------------------------------------------------
    //----------------------------------------------------------
    public render(): JSX.Element | null {

        const isAvailable = this.props.data.sharedAvailabilityState.result?.isAvailable;

        // Create the view props
        const viewProps: ISubstituteProductsViewProps = {
            ...this.props,
            productList: this.constructProductList(),
            showSubstitute: !isAvailable,
        };

        // Pass along to the view
        return this.props.renderView(viewProps);
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    private constructProductList(): SubstituteProduct[] | undefined {
        if (this.props.data.substituteProducts.result) {
            const output = this.props.data.substituteProducts.result.map(entry => ({
                name: entry.Name || '',
                id: entry.RecordId,
                price: this.formatPrice(entry),
            }));

            // Limit to requested number of entries
            return output.slice(0, this.props.config.maxProductsToShow);
        }

        return undefined;
    }

    //----------------------------------------------------------
    //----------------------------------------------------------
    private formatPrice(product: ProductSearchResult): string {
        const format = this.props.context.cultureFormatter.formatCurrency;

        // Determine if we should use min/max or price
        if (product.MinVariantPrice && product.MaxVariantPrice) {

            // If min and max are the same, display either one
            if (product.MinVariantPrice === product.MaxVariantPrice) {
                return format(product.MinVariantPrice);
            }

            // We have a range -- this hard-codes the format which isn't ideal!
            return `${format(product.MinVariantPrice)} - ${format(product.MaxVariantPrice)}`;
        }

        // If there's a price, use it
        if (product.Price) {
            return format(product.Price);
        }

        // There's no price. Use the No Price string from resources.
        // If would be much better to pull this from an existing module, but that's a bit challenging.
        return this.props.resources.priceFree;
    }
}

export default SubstituteProducts;
